import dayjs from "dayjs"
import { DateString, formatDate } from "@/utils/dateString"
import { displayWeekdayJa } from "@/utils/displayWeekday"

export interface TransportationExpense {
    id: string
    expenseDate: DateString
    amount: number
    routeDescription: string
    createdAt: number
    updatedAt: number | null
}

export const displayExpenseDate = (expenseDate: DateString) => {
    const date = dayjs(expenseDate)
    return `${date.format("YYYY/MM/DD")} (${displayWeekdayJa(date.day())})`
}

/** 「2026/6/1」形式 */
export const displayDateWithYear = (date: DateString) =>
    dayjs(date).format("YYYY/M/D")

export const displayExpenseTimestamp = (unixSeconds: number) =>
    dayjs.unix(unixSeconds).format("YYYY/MM/DD HH:mm")

// 金額の下限・上限。バックエンドの値と同値にしておくこと。
// 下限0は「自転車移動などで交通費0円」「誤入力を0に戻す」ケースを許容するため（GENM-3567）。
// 上限は誤入力ケア目的。
export const AMOUNT_MIN = 0
export const AMOUNT_MAX = 3000
export const ROUTE_DESCRIPTION_MAX_CHARS = 500

export const INVALID_AMOUNT_MESSAGE = `金額は${AMOUNT_MIN}〜${AMOUNT_MAX.toLocaleString("ja-JP")}円で入力してください`
export const INVALID_ROUTE_DESCRIPTION_MESSAGE = `経路は${ROUTE_DESCRIPTION_MAX_CHARS}文字以内で入力してください`
export const EXPENSE_DATE_OUT_OF_RANGE_MESSAGE =
    "申請できるのは当日または前日の分のみです"

export type ValidationResult<T> =
    { ok: true; value: T } | { ok: false; message: string }

export const validateAmount = (raw: string): ValidationResult<number> => {
    const trimmed = raw.trim()
    if (trimmed === "") {
        return { ok: false, message: "金額を入力してください" }
    }
    if (!/^[0-9]+$/.test(trimmed)) {
        return { ok: false, message: "金額は半角数字で入力してください" }
    }
    const amount = Number(trimmed)
    if (amount < AMOUNT_MIN || amount > AMOUNT_MAX) {
        return { ok: false, message: INVALID_AMOUNT_MESSAGE }
    }
    return { ok: true, value: amount }
}

/**
 * 経路の文字数を数える。
 * バックエンド(Rust)の chars().count() に合わせて、trim後のコードポイント数で数える。
 * 入力欄のカウンター表示とバリデーションの両方でこの関数を使うこと。
 */
export const countRouteDescriptionChars = (raw: string): number =>
    [...raw.trim()].length

export const validateRouteDescription = (
    raw: string
): ValidationResult<string> => {
    const trimmed = raw.trim()
    if (trimmed === "") {
        return { ok: false, message: "経路を入力してください" }
    }
    if (countRouteDescriptionChars(raw) > ROUTE_DESCRIPTION_MAX_CHARS) {
        return { ok: false, message: INVALID_ROUTE_DESCRIPTION_MESSAGE }
    }
    return { ok: true, value: trimmed }
}

/**
 * 申請フォーム・修正ダイアログ共通の入力バリデーション。
 * 金額→経路の順に検証し、最初のエラーを返す。
 */
export const validateExpenseInput = (
    amountRaw: string,
    routeDescriptionRaw: string
): ValidationResult<{ amount: number; routeDescription: string }> => {
    const amountResult = validateAmount(amountRaw)
    if (!amountResult.ok) {
        return amountResult
    }
    const routeResult = validateRouteDescription(routeDescriptionRaw)
    if (!routeResult.ok) {
        return routeResult
    }
    return {
        ok: true,
        value: {
            amount: amountResult.value,
            routeDescription: routeResult.value
        }
    }
}

/** 申請可能な最も古い日付（前日）を返す */
export const minSelectableExpenseDate = (today: DateString): DateString =>
    formatDate(dayjs(today).subtract(1, "day"))

export const validateExpenseDate = (
    date: DateString,
    today: DateString
): ValidationResult<DateString> => {
    // DateStringはYYYY-MM-DD形式のため辞書順比較で大小判定できる
    if (date < minSelectableExpenseDate(today) || date > today) {
        return { ok: false, message: EXPENSE_DATE_OUT_OF_RANGE_MESSAGE }
    }
    return { ok: true, value: date }
}

/**
 * 申請内容（金額・経路）を修正できるか。
 * 修正できるのは清掃日の翌日まで（＝申請可能期間と同じ範囲）。
 */
export const isExpenseEditable = (
    expenseDate: DateString,
    today: DateString
): boolean => validateExpenseDate(expenseDate, today).ok

/** 申請履歴として表示する最も古い日付（前月1日）を返す */
export const oldestVisibleExpenseDate = (today: DateString): DateString =>
    formatDate(dayjs(today).startOf("month").subtract(1, "month"))

export interface MonthlyExpenseGroup {
    /** YYYY-MM */
    yearMonth: string
    expenses: TransportationExpense[]
    totalAmount: number
}

/**
 * 申請を月ごとにまとめ、月合計金額を添えて新しい月から順に返す。
 * 給与明細との突合が履歴を見る主目的のため、月合計を出せる形にしておく。
 */
export const groupExpensesByMonth = (
    expenses: TransportationExpense[]
): MonthlyExpenseGroup[] => {
    const groups = new Map<string, TransportationExpense[]>()

    expenses.forEach(expense => {
        // DateStringはYYYY-MM-DD形式のため先頭7文字がYYYY-MM
        const yearMonth = expense.expenseDate.slice(0, 7)
        const group = groups.get(yearMonth)
        if (group) {
            group.push(expense)
        } else {
            groups.set(yearMonth, [expense])
        }
    })

    return [...groups.entries()]
        .sort(([a], [b]) => (a < b ? 1 : -1))
        .map(([yearMonth, groupedExpenses]) => ({
            yearMonth,
            expenses: [...groupedExpenses].sort((a, b) =>
                a.expenseDate < b.expenseDate ? 1 : -1
            ),
            totalAmount: groupedExpenses.reduce(
                (total, expense) => total + expense.amount,
                0
            )
        }))
}

/** YYYY-MM を「2026年7月」形式にする */
export const displayYearMonth = (yearMonth: string): string => {
    const [year, month] = yearMonth.split("-")
    return `${year}年${Number(month)}月`
}

export const findExpenseOnDate = (
    expenses: TransportationExpense[],
    date: DateString
): TransportationExpense | undefined =>
    expenses.find(expense => expense.expenseDate === date)
